import { useTranslation } from "react-i18next";
import { SearchNormal1, Calendar2, Refresh2 } from "iconsax-react";
import { Select, Input, DatePicker } from "@/shared/ui";
import type { Locale, DateRange } from "@/shared/utils";
import type { AdvanceStatus, AdvanceListType } from "../../../types";

interface Props {
  search: string;
  onSearchChange: (value: string) => void;
  typeFilter: AdvanceListType | null;
  onTypeChange: (value: AdvanceListType | null) => void;
  statusFilter: AdvanceStatus | null;
  onStatusChange: (value: AdvanceStatus | null) => void;
  dateRange: DateRange;
  onDateRangeChange: (value: DateRange) => void;
  locale: Locale;
  hasActiveFilters: boolean;
  onReset: () => void;
}

export default function AdvancesFilter({
  search,
  onSearchChange,
  typeFilter,
  onTypeChange,
  statusFilter,
  onStatusChange,
  dateRange,
  onDateRangeChange,
  locale,
  hasActiveFilters,
  onReset,
}: Props) {
  const { t } = useTranslation("AdvancesPage");

  const typeOptions = [
    { label: t("filters.allTypes"), value: null as AdvanceListType | null },
    { label: t("filters.typeAdvance"), value: "advance" as AdvanceListType },
    { label: t("filters.typeTrip"), value: "trip" as AdvanceListType },
  ];

  const statusOptions = [
    { label: t("filters.allStatuses"), value: null as AdvanceStatus | null },
    { label: t("statusLabels.draft"), value: "draft" as AdvanceStatus },
    { label: t("statusLabels.pending"), value: "pending" as AdvanceStatus },
    { label: t("statusLabels.approved"), value: "approved" as AdvanceStatus },
    { label: t("statusLabels.order_acknowledged"), value: "order_acknowledged" as AdvanceStatus },
    { label: t("statusLabels.rejected"), value: "rejected" as AdvanceStatus },
    { label: t("statusLabels.revision"), value: "revision" as AdvanceStatus },
    { label: t("statusLabels.issued"), value: "issued" as AdvanceStatus },
    { label: t("statusLabels.settled"), value: "settled" as AdvanceStatus },
  ];

  function handleDateRangeChange(v: Date | { start: Date | null; end: Date | null } | null) {
    if (!v || v instanceof Date) return;
    onDateRangeChange(v);
  }

  return (
    <div className="flex items-center justify-between gap-2 mb-7">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="min-w-48">
          <Input
            isSmall
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("filters.search")}
            icon={<SearchNormal1 size={14} color="currentColor" />}
          />
        </div>

        <div className="min-w-36">
          <Select<AdvanceListType | null>
            options={typeOptions}
            value={typeFilter}
            onChange={onTypeChange}
            isSmall
            width="w-40"
          />
        </div>

        <div className="min-w-36">
          <Select<AdvanceStatus | null>
            options={statusOptions}
            value={statusFilter}
            onChange={onStatusChange}
            isSmall
            width="w-44"
          />
        </div>

        <DatePicker
          value={dateRange}
          mode="range"
          onChange={handleDateRangeChange}
          locale={locale}
          placeholder={t("filters.allTime")}
          isSmall
          hasPeriodPicker
          width="min-w-40"
          icon={<Calendar2 size={16} color="currentColor" />}
          align="left"
        />

        {hasActiveFilters && (
          <button
            type="button"
            className="px-3 h-8 flex justify-center items-center gap-2 content-base-low cursor-pointer"
            onClick={onReset}>
            <Refresh2 size={16} color="currentColor" />
            <span className="text-label-sm">{t("filters.reset")}</span>
          </button>
        )}
      </div>
    </div>
  );
}
